<template>
  <div>
    <a-row justify="space-between" v-if="tableConfig.isShowTools">
      <a-col :span="16">
        <slot name="leftButton"></slot>
      </a-col>
      <a-col :span="8" style="text-align: right;">
        <slot name="rightButton"></slot>
        <!-- 高级查询 -->
        <j-super-query v-if="advancedSearch"
                       :fieldList="advancedSearchField"
                       style="margin-right: 8px"
                       @handleSuperQuery="advancedSearchBack" />
        <!-- 重置按钮 -->
        <mtc-button icon="reload" text="button.reset" type="primary" @click="$emit('searchReset')" />
        <!-- 查询按钮 -->
        <mtc-button icon="search" style="margin-left: 8px" text="button.search" type="primary" @click="refresh(1)" />
      </a-col>
    </a-row>
    <a-alert v-if="tableConfig.isShowAlert" style="margin-top: 10px">
      <template slot="message">
        <a-icon class="anticon ant-alert-icon" theme="filled" type="info-circle" />
        <span style="margin-left: 20px">
          <!-- 已选择 -->
          <mtcLangPopover text="tip.selected" />：
          <!-- 条数 -->
          <a style="font-weight: 600">{{ selectedRowKeys.length }}</a>
          <!-- 单位 -->
          <mtcLangPopover text="tip.term" />
          <mtcLangPopover text="button.clear">
            <a style="margin-left: 24px" @click="clearSelected">{{ $t('button.clear') }}</a>
          </mtcLangPopover>
        </span>
      </template>
    </a-alert>
    <a-table ref="baseTable"
             style="margin-top: 10px;"
             :bordered="tableConfig.isBordered"
             :columns="tableColumns"
             :dataSource="dataSource"
             :loading="isLoading"
             :pagination="pagination"
             :rowKey="tableConfig.rowKey"
             :rowSelection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
             :scroll="{ x: 'fit-content', y: tableHeight  }"
             :size="tableConfig.size"
             class="j-table-force-nowrap"
             @change="handleTableChange">
      <!-- 表头自定义渲染 -->
      <template v-for="(item, index) in tableColumns" :slot="item.slotName">
        <mtcHeader :key="index" :text="item.slotName" />
      </template>
      <!-- 列表数据自定义渲染 -->
      <template v-for="item in renderColumns" :slot="item.dataIndex" slot-scope="text, record">
        <mtc-lang-list-popover :key="item.dataIndex" :item="item" :record="record" :text="text" />
      </template>
      <!-- 父组件传入 -->
      <template v-for="item in Object.keys($scopedSlots)"
                :slot="item"
                slot-scope="text, record, index">
        <slot :name="item" :record="record, index"></slot>
      </template>
    </a-table>
  </div>
</template>

<script>
/*
* @auth 郭超
* @desc 基础列表组件
* @date 2022-6-10
* */
import { getLanguageField, getTableDataLang } from '@/utils/langUtils'

export default {
  name: 'BaseTable',
  props: {
    // 表名
    mainTable: {
      type: String,
      default: ''
    },
    // 是否要高级查询
    advancedSearch: {
      type: [Boolean, Array],
      default: false
    },
    // 列表配置
    tableSetting: {
      type: Object,
      default: () => {
      }
    },
    // 分页配置 传false为不展示分页
    pageSetting: {
      type: [Object, Boolean],
      default: () => {
      }
    },
    // 表头
    tableColumns: {
      type: Array,
      require: true,
      default: () => []
    },
    // 列表数据，固定数组/请求方法的promise
    tableData: {
      type: [Array, Function],
      require: true
    }
  },
  data() {
    return {
      // 列表默认配置
      baseTableConfig: {
        // 列表尺寸
        size: 'middle',
        // 是否要框线
        isBordered: true,
        // 主键字段
        rowKey: 'id',
        // 是否展示列表上方alert组件
        isShowAlert: true,
        // 是否展示工具栏
        isShowTools: true
      },
      // 分页配置
      basePageConfig: {
        // 当前页面
        current: 1,
        // 每页展示条数
        pageSize: 30,
        total: 0,
        showQuickJumper: true,
        showSizeChanger: true,
        pageSizeOptions: ['10', '30', '50', '100'],
        showTotal: (total, range) => {
          return `${range[ 0 ]}-${range[ 1 ]}${this.$t('tip.total')}${total}${this.$t('tip.article')}`
        }
      },
      // 列表加载状态
      isLoading: false,
      // 列表数据内容
      dataSource: [],
      // 选中主键数据
      selectedRowKeys: [],
      // 选中整行数据
      selectionRows: [],
      // 高级查询字段
      advancedSearchField: [],
      // 高级查询条件
      superQueryParams: '',
      // 高级查询logic
      superQueryMatchType: '',
      // 当前列表中开启数据多语言的字段
      tableLangFieldArr: [],
      tablePage: {}
    }
  },
  computed: {
    // 列表高度
    tableHeight() {
      if (this.tableConfig.height) return this.tableConfig.height
      return window.innerHeight - 252
    },
    // 列表配置
    tableConfig() {
      return {
        ...this.baseTableConfig,
        ...this.tableSetting
      }
    },
    // 分页配置
    pagination() {
      if (typeof this.pageSetting === 'boolean') return this.pageSetting
      return {
        ...this.basePageConfig,
        ...this.pageSetting,
        ...this.tablePage
      }
    },
    // 开启数据多语言的字段才进行气泡的自定义渲染
    renderColumns() {
      return this.tableColumns.filter(ele => this.tableLangFieldArr.includes(ele.dataIndex))
    }
  },
  created() {
    this.getListLangField(this.mainTable)
    this.setAdvancedSearchField()
  },
  methods: {
    // 根据表名获取当前列表多语言字段
    getListLangField(tableName) {
      if (!tableName) return this.loadData()
      getLanguageField(tableName).then(res => {
        this.tableLangFieldArr = res.fieldArr
        this.$store.commit('SET_TABLELANGFIELD', this.tableLangFieldArr)
        this.loadData()
      })
    },
    // 数据加载
    loadData() {
      // 静态数据处理
      if (typeof this.tableData !== 'function') {
        this.dataSource = this.tableData
        return
      }
      let params = {
        langType: this.$ls.get('language'),
        pageNo: this.pagination.current,
        pageSize: this.pagination.pageSize
      }
      // 高级查询参数
      if (this.advancedSearch) {
        params.superQueryParams = encodeURI(this.superQueryParams)
        params.superQueryMatchType = this.superQueryMatchType
      }
      this.isLoading = true
      this.tableData(params).then(res => {
        this.isLoading = false
        const result = res.result || {}
        // total处理
        this.basePageConfig.total = result?.total || 0
        this.dataSource = result.records || result
        let ids = this.dataSource.map(ele => ele.id).toString()
        ids && getTableDataLang(ids, this.dataSource, this.tableLangFieldArr).then(res => {
          if (res) this.dataSource = res
        })
      }).catch(e => this.isLoading = false)
    },
    // 高级查询查询回调
    advancedSearchBack(params, matchType) {
      //高级查询方法
      if (!params.length) {
        this.superQueryParams = ''
      } else {
        this.superQueryParams = JSON.stringify(params)
        this.superQueryMatchType = matchType
      }
      this.refresh(1)
    },
    // 查询
    refresh(page = 1) {
      this.basePageConfig.current = page
      this.loadData()
    },
    // 根据表头获取高级查询字段
    setAdvancedSearchField() {
      // 如果父组件不传高级查询字段，则自动获取列中的字段作为高级查询字段
      if (typeof this.advancedSearch === 'boolean') {
        if (!this.advancedSearch) return
        let arr = []
        this.tableColumns.forEach(ele => {
          // 过滤掉序号列和操作列
          if (ele.dataIndex && (!['action', 'operation'].includes(ele.dataIndex))) {
            arr.push({
              type: ele.type,
              value: ele.dataIndex,
              text: this.$t(ele.slotName)
            })
          }
        })
        this.advancedSearchField = arr
      } else {
        this.advancedSearchField = this.advancedSearch
      }
    },
    // 分页、排序、筛选变化时触发
    handleTableChange(pagination, filters, sorter) {
      if (Object.keys(sorter).length > 0) {
        this.isorter.column = sorter.field
        this.isorter.order = sorter.order === 'ascend'? 'asc': 'desc'
      }
      this.tablePage = pagination
      this.loadData()
    },
    // 获取列表数据
    getData() {
      return this.dataSource
    },
    // 列表复选框选中
    onSelectChange(selectedRowKeys, selectionRows) {
      this.selectedRowKeys = selectedRowKeys
      this.selectionRows = selectionRows
      this.$emit('selectRow', selectedRowKeys, selectionRows)
    },
    // 清空选中数据
    clearSelected() {
      this.selectedRowKeys = []
      this.selectionRows = []
    }
  }
}
</script>